/**
 * Contact form. The Shopify `form` tag is only an envelope: the submit is intercepted and the
 * payload POSTed to the `/apps/contact` proxy, which files the ticket in Zendesk. The proxy expects
 * exactly these seven keys, so `data-jd-field` names are a contract — do not rename them.
 * Without this script the native Shopify form still posts, so the page degrades instead of breaking.
 */
class JdContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form || this.form.dataset.jdBound === 'true') return;
    this.form.dataset.jdBound = 'true';

    this.fieldsPanel = this.querySelector('[data-jd-contact-fields]');
    this.successPanel = this.querySelector('[data-jd-contact-success]');
    this.globalError = this.querySelector('[data-jd-contact-error]');
    this.submitButton = this.querySelector('[data-jd-contact-submit]');
    this.controls = Array.from(this.querySelectorAll('[data-jd-field]'));

    // Custom messages replace the browser bubbles only once the script is live.
    this.form.noValidate = true;

    this.form.addEventListener('submit', (event) => this.#handleSubmit(event));
    this.controls.forEach((control) => {
      control.addEventListener('input', () => this.#clearError(control));
      control.addEventListener('change', () => this.#clearError(control));
    });

    this.querySelectorAll('[data-jd-autogrow]').forEach((textarea) => {
      textarea.addEventListener('input', () => this.#autoGrow(textarea));
    });
  }

  #autoGrow(textarea) {
    textarea.style.blockSize = 'auto';
    textarea.style.blockSize = `${textarea.scrollHeight}px`;
  }

  #messageFor(control) {
    const field = control.closest('.jd-field');
    return field ? field.querySelector('[data-jd-field-message]') : null;
  }

  #showError(control, text) {
    const message = this.#messageFor(control);
    control.setAttribute('aria-invalid', 'true');
    if (!message) return;
    message.textContent = text;
    message.hidden = false;
  }

  #clearError(control) {
    const message = this.#messageFor(control);
    control.removeAttribute('aria-invalid');
    if (!message) return;
    message.textContent = '';
    message.hidden = true;
  }

  #validate() {
    let firstInvalid = null;

    this.controls.forEach((control) => {
      this.#clearError(control);
      if (control.checkValidity()) return;

      const dataset = control.dataset;
      const text = control.validity.valueMissing
        ? dataset.jdErrorRequired || ''
        : dataset.jdErrorInvalid || dataset.jdErrorRequired || '';
      this.#showError(control, text);
      if (!firstInvalid) firstInvalid = control;
    });

    if (firstInvalid) firstInvalid.focus();
    return !firstInvalid;
  }

  #setGlobalError(text) {
    if (!this.globalError) return;
    this.globalError.textContent = text || '';
    this.globalError.hidden = !text;
  }

  #payload() {
    const inputs = {};
    this.controls.forEach((control) => {
      inputs[control.dataset.jdField] = control.value.trim();
    });
    return { inputs };
  }

  async #handleSubmit(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    this.#setGlobalError('');
    if (!this.#validate()) return;

    this.submitButton.disabled = true;

    try {
      const response = await fetch('/apps/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#payload()),
      });

      if (response.status === 200) {
        this.fieldsPanel.hidden = true;
        this.successPanel.hidden = false;
        this.successPanel.focus();
        return;
      }

      this.#setGlobalError(this.dataset.errorMessage);
    } catch {
      this.#setGlobalError(this.dataset.errorMessage);
    } finally {
      this.submitButton.disabled = false;
    }
  }
}

if (!customElements.get('jd-contact-form')) {
  customElements.define('jd-contact-form', JdContactForm);
}
